import React, { useCallback, useEffect, useState } from "react";
import { AddIcon, Alert, Button, InlineAlert, Pane } from "evergreen-ui";
import currency from "currency.js";
import AccountingInputSplit from "./AccountingInputSplit";
import { formatCurrency } from "../../utils/formatters";
import useControlHub from "../../hooks/useControlHub";

const AccountingInput = ({ amount, onDataChange, splits }) => {
  const { api } = useControlHub();
  const [categories, setCategories] = useState();
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    api
      .getAccountingCategories()
      .then((data) => {
        if (!data) {
          throw new Error(
            "Something went wrong while retrieving accounting categories"
          );
        }
        setCategories(data);
      })
      .catch(() => {
        setCategories();
      });
  }, [api]);

  useEffect(() => {
    if (!splits || splits.length) {
      return;
    }
    const newSplit = {};
    newSplit["amount"] = currency(amount);
    newSplit["id"] = "";
    newSplit["account_categories"] = [];
    onDataChange({ splits: [newSplit] });
  }, [amount, onDataChange, splits]);

  useEffect(() => {
    if (
      splits?.length !== 1 ||
      currency(splits[0]["amount"]).intValue === currency(amount).intValue
    ) {
      return;
    }
    onDataChange({ splits: [{ ...splits[0], amount: currency(amount) }] });
  }, [amount, onDataChange, splits]);

  useEffect(() => {
    if (splits?.length) {
      setRemaining(
        splits.reduce(
          (prev, split) => prev.subtract(currency(split.amount)),
          currency(amount)
        )
      );
    }
  }, [amount, splits]);

  const handleAddSplitButton = useCallback(() => {
    const newSplit = {};
    newSplit["amount"] = currency(0);
    newSplit["id"] = "";
    newSplit["account_categories"] = [];
    onDataChange({ splits: [...splits, newSplit] });
  }, [onDataChange, splits]);

  const handleSplitChange = useCallback(
    (index) => (split) => {
      const _splits = [];
      _splits.push(...splits.slice(0, index));
      if (split) {
        _splits.push(split);
      }
      _splits.push(...splits.slice(index + 1));
      onDataChange({ splits: _splits });
    },
    [onDataChange, splits]
  );

  if (!categories) {
    return <Alert intent="warning" title="Unable to render this section." />;
  }

  return (
    <Pane flexDirection="column" display="flex">
      {splits?.map((split, index) => (
        <AccountingInputSplit
          {...split}
          categories={categories}
          hasAmount={splits.length > 1}
          isDeletable={splits.length > 1}
          key={`Accounting-Split_${index}`}
          marginBottom={16}
          onSplitChange={handleSplitChange(index)}
        />
      ))}
      <Pane alignItems="center" display="flex" justifyContent="space-between">
        <Pane flex="1 0 auto">
          <Button iconBefore={AddIcon} onClick={handleAddSplitButton}>
            Add new split
          </Button>
        </Pane>
        <Pane paddingLeft={16}>
          {remaining.value !== 0 ? (
            <InlineAlert intent="none">{`${formatCurrency(
              remaining * 100
            )} left to assign`}</InlineAlert>
          ) : null}
        </Pane>
      </Pane>
    </Pane>
  );
};

export default AccountingInput;
